import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { 
  Search, 
  Trash, 
  Loader2,
  AlertCircle,
  Mail,
  Phone,
  Building2,
  User
} from "lucide-react";
import { format } from "date-fns";
import { api } from "@/lib/api";
import { 
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const MessagesAdmin = () => {
  const [search, setSearch] = useState("");
  const [deletingMessageId, setDeletingMessageId] = useState<string | null>(null);
  
  const queryClient = useQueryClient();
  
  const { 
    data: messages = [], 
    isLoading, 
    isError 
  } = useQuery({
    queryKey: ['messages'],
    queryFn: api.contact.getAll
  });
  
  const filteredMessages = messages.filter((message: any) =>
    message.name.toLowerCase().includes(search.toLowerCase()) ||
    message.email.toLowerCase().includes(search.toLowerCase()) ||
    message.company?.toLowerCase().includes(search.toLowerCase()) ||
    message.message.toLowerCase().includes(search.toLowerCase())
  );

  const handleDeleteMessage = async () => {
    if (deletingMessageId) {
      await api.contact.delete(deletingMessageId);
      setDeletingMessageId(null);
      queryClient.invalidateQueries({ queryKey: ['messages'] });
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-3xl font-bold">Сообщения</h1>
      </div>
      
      <div className="relative w-full max-w-sm">
        <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
        <Input
          type="search"
          placeholder="Поиск сообщений..."
          className="pl-8"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>
      
      {isLoading ? (
        <div className="flex justify-center items-center py-8">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      ) : isError ? (
        <div className="flex justify-center items-center py-8 text-destructive">
          <AlertCircle className="h-8 w-8 mr-2" />
          <p>Ошибка загрузки сообщений. Пожалуйста, попробуйте позже.</p>
        </div>
      ) : filteredMessages.length === 0 ? (
        <div className="text-center py-8 text-muted-foreground">
          Сообщений не найдено
        </div>
      ) : (
        <div className="border rounded-lg">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Отправитель</TableHead>
                <TableHead>Контактная информация</TableHead>
                <TableHead>Сообщение</TableHead>
                <TableHead>Дата</TableHead>
                <TableHead className="w-[100px]">Действия</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredMessages.map((message: any) => (
                <TableRow key={message.id}>
                  <TableCell>
                    <div className="flex items-center">
                      <User className="h-4 w-4 mr-2 text-muted-foreground" />
                      {message.name}
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="space-y-1">
                      <div className="flex items-center">
                        <Mail className="h-4 w-4 mr-2 text-muted-foreground" />
                        <a href={`mailto:${message.email}`} className="text-primary hover:underline">
                          {message.email}
                        </a>
                      </div>
                      {message.phone && (
                        <div className="flex items-center">
                          <Phone className="h-4 w-4 mr-2 text-muted-foreground" />
                          <a href={`tel:${message.phone}`} className="text-primary hover:underline">
                            {message.phone}
                          </a>
                        </div>
                      )}
                      {message.company && (
                        <div className="flex items-center">
                          <Building2 className="h-4 w-4 mr-2 text-muted-foreground" />
                          {message.company}
                        </div>
                      )}
                    </div>
                  </TableCell>
                  <TableCell className="whitespace-pre-wrap break-words">
                    {message.message}
                  </TableCell>
                  <TableCell>
                    {format(new Date(message.created_at), 'dd.MM.yyyy HH:mm')}
                  </TableCell>
                  <TableCell>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => setDeletingMessageId(message.id)}
                    >
                      <Trash className="h-4 w-4 text-destructive" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
      
      <AlertDialog open={!!deletingMessageId} onOpenChange={() => setDeletingMessageId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Удалить сообщение?</AlertDialogTitle>
            <AlertDialogDescription>
              Это действие нельзя отменить. Сообщение будет удалено навсегда.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Отмена</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteMessage} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              Удалить
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default MessagesAdmin; 